import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { Card } from '../components/Card'
import logo from '../assets/El CapIT_logo_top_left.png'

export default function CustomerLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [needsNewPassword, setNeedsNewPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const { login, completeNewPassword } = useAuth()
  const { tenant } = useTenant()
  const navigate = useNavigate()

  // Force light mode on login page
  useEffect(() => {
    document.documentElement.classList.remove('dark')
    return () => {
      const stored = localStorage.getItem('user-theme-preference')
      if (stored === 'dark') {
        document.documentElement.classList.add('dark')
      }
    }
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (tenant?.status === 'SUSPENDED') {
      setError('This gym account is currently suspended. Please contact support.')
      return
    }

    try {
      setError('')
      setLoading(true)
      await login(email, password)
      navigate('/customer/dashboard')
    } catch (err: any) {
      if (err.message === 'NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true)
      } else {
        setError(err.message || 'Failed to sign in. Please check your credentials.')
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleNewPasswordSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    try {
      setError('')
      setLoading(true)
      await completeNewPassword(newPassword)
      navigate('/customer/dashboard')
    } catch (err: any) {
      setError(err.message || 'Failed to set new password')
    } finally {
      setLoading(false)
    }
  }

  // Use gym logo if available, otherwise fall back to El CapIT logo
  const gymLogo = tenant?.branding?.logoUrl
  const gymName = tenant?.branding?.gymName

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {gymLogo ? (
            <img src={gymLogo} alt={gymName} className="h-24 mx-auto mb-6 object-contain" />
          ) : (
            <img src={logo} alt="El CapIT" className="h-48 mx-auto mb-6" />
          )}
        </div>

        <Card padding="lg">
          {!needsNewPassword ? (
            <>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6">
                Sign In
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-error-light/20 border border-error-DEFAULT rounded-lg">
                  <p className="text-sm text-error-dark">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="email"
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />

                <Input
                  type="password"
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={loading}
                  loading={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 text-center space-y-2">
                <p className="text-sm text-neutral-500">
                  <Link to="/customer/forgot-password" className="font-medium text-primary-600 hover:underline">
                    Forgot your password?
                  </Link>
                </p>
                <p className="text-sm text-neutral-500">
                  Don't have an account?{' '}
                  <Link to="/customer/signup" className="font-medium text-primary-600 hover:underline">
                    Create one
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Set New Password</h2>
              <p className="text-neutral-600 mb-6">Please set a new password for your account</p>

              {error && (
                <div className="mb-6 p-4 bg-error-light/20 border border-error-DEFAULT rounded-lg">
                  <p className="text-sm text-error-dark">{error}</p>
                </div>
              )}

              <form onSubmit={handleNewPasswordSubmit} className="space-y-6">
                <Input
                  type="password"
                  label="New Password"
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />

                <Input
                  type="password"
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={loading}
                  loading={loading}
                >
                  {loading ? 'Setting password...' : 'Set New Password'}
                </Button>
              </form>
            </>
          )}
        </Card>

        {gymName && (
          <p className="text-center text-sm text-neutral-500">{gymName} · Powered by El CapIT<br />A BuilderBias product</p>
        )}
      </div>
    </div>
  )
}
